import { FC } from "react"
import { useNavigate } from "react-router-dom"

import NodesIcon from "@mui/icons-material/DnsOutlined"
import UnreadIcon from "@mui/icons-material/FiberManualRecord"
import UCIcon from "@mui/icons-material/SentimentVeryDissatisfied"
import CameraDownIcon from "@mui/icons-material/VideocamOffOutlined"
import CamIcon from "@mui/icons-material/VideocamOutlined"

import { Tooltip, Typography } from "@synapse-analytics/synapse-ui"
import { format } from "date-fns"
import moment from "moment"

import { routes } from "../../../routes/routes"
import { definitions } from "../../../types/Generated/apiTypes"
import auth from "../../../utils/auth"

import styles from "./NotificationCard.module.scss"

type Notification = definitions["Notification"]

interface Props {
  notification?: Notification
  handleCloseNotificationsMenu: () => void
  handleReadNotification: ({ notificationId }: { notificationId?: number }) => void
}

const getNotificationIcon = (type: string, extra?: Notification["extra"]) => {
  switch (type) {
    case "CVC":
      return <CamIcon className={styles.notificationIcon} />
    case "CSC":
      return extra?.state === "up" ? (
        <CamIcon className={styles.notificationIcon} />
      ) : (
        <CameraDownIcon className={styles.notificationIcon} />
      )
    case "NSC":
      return <NodesIcon className={styles.notificationIcon} />
    case "UC":
      return <UCIcon className={styles.notificationIcon} />
    default:
      return null
  }
}

const NotificationCard: FC<Props> = ({ notification, handleCloseNotificationsMenu, handleReadNotification }) => {
  const navigate = useNavigate()

  const handleNotificationAction = () => {
    handleReadNotification({ notificationId: notification?.id })
    const { type, extra } = notification || {}

    const notificationRoutes = {
      CSC: () => {
        if (auth.hasFeaturePermission("cameras")) {
          navigate(
            extra?.id
              ? `/${routes.cameraList}/admin-camera-details/${extra.id}?branch=${notification?.branch}`
              : `/${routes.cameraList}?branch=${notification?.branch}&filterStatus=camerasDown%3Atrue`,
            {
              state: { id: extra?.id },
            }
          )
        }
      },
      CVC: () => {
        if (auth.hasFeaturePermission("cameras")) {
          navigate(`/${routes.cameraViewChanges}?branch=${notification?.branch}`)
        }
      },
      NSC: () => {
        if (auth.hasFeaturePermission("nodes")) {
          if (notification?.extra?.state === "down") {
            navigate(`/${routes.nodesList}?branch=${notification?.branch}`)
          } else {
            navigate(`/${routes.nodeDownAnalytics}/${extra?.id}?branch=${notification?.branch}`, {
              state: {
                name: extra?.name,
                id: extra?.id,
              },
            })
          }
        }
      },
    }

    notificationRoutes[type as keyof typeof notificationRoutes]?.()

    handleCloseNotificationsMenu()
  }

  const renderNotificationText = () => {
    const typeText = {
      CSC: notification?.extra?.state === "down" ? "is down" : "is now up",
      NSC: notification?.extra?.state === "down" ? "is down" : "is now up",
      CVC: "view has changed",
      UC: "has unserved customer",
    }

    const nameFallback = {
      CVC: "A Camera",
      CSC: "A Camera",
      NSC: "A Node",
      UC: "An Entity",
    }

    const entityName = notification?.extra?.name || nameFallback[notification?.type as keyof typeof nameFallback]

    return (
      <Typography variant="p" variantColor={notification?.read ? 2 : 1}>
        <b>{entityName}</b> from <b>{notification?.branch_name} </b>
        {typeText[notification?.type as keyof typeof typeText]}
      </Typography>
    )
  }

  return (
    <div className={styles.wrapper} onClick={handleNotificationAction}>
      <div className={styles.iconAndContent}>
        {notification?.type && <div>{getNotificationIcon(notification.type, notification.extra)}</div>}

        <div className={styles.contentWrapper}>
          {renderNotificationText()}
          {notification?.timestamp && (
            <Tooltip title={format(new Date(notification.timestamp), "dd/MM/yyyy, p")} placement="left">
              <Typography variant="span-bold" variantColor={2} style={{ width: "fit-content" }}>
                {moment(notification.timestamp).fromNow()}
              </Typography>
            </Tooltip>
          )}
        </div>
      </div>

      {!notification?.read && <UnreadIcon className={styles.unreadIcon} />}
    </div>
  )
}

export default NotificationCard
